/**
 * 己方底牌卡（Optical 重构）：「玻璃数据晶片 + 金属标题 + 发丝分割 + 折叠锦囊」。
 *
 * 视觉目标：
 * - 卡片内不再出现二三层实线小框，改用 75% 透镜内的明度阶梯与发丝分割线；
 * - 阵营色只作为点光源（状态点/左侧微光/关键词晶片），不做大面积彩色平涂；
 * - 卡面挂载低透明度 Matrix Orb，让「底牌」本身成为能量界面的一部分。
 */
import {
  ARCHETYPE_LABEL,
  SANDBOX_LABEL,
  sideLabel,
  type ConstructiveStrategyCard,
  type CrossExQuestionChain,
  type CrossExTaskCard,
  type DealtCard,
  type SandboxCard,
  type SandboxPerspective,
  type Side,
} from '@zongheng/core-schema';
import type { ReactNode } from 'react';
import { Badge, cx, EmptyState, Panel } from '../ui/primitives.tsx';
import { MatrixOrb } from '../system/MatrixOrb.tsx';

export interface CardPanelProps {
  card: DealtCard | null;
  selfSide: Side;
  checkedEvidenceIds: readonly string[];
  trapOpen: boolean;
  onToggleTrap: () => void;
  onToggleEvidence: (evidenceId: string) => void;
}

export function CardPanel({
  card,
  selfSide,
  checkedEvidenceIds,
  trapOpen,
  onToggleTrap,
  onToggleEvidence,
}: CardPanelProps) {
  return (
    <Panel
      testId="hud-card"
      accent={selfSide}
      title="己方底牌 · 仅本侧可见"
      subtitle={
        card ? '双盲发牌：对方卡片需等待本环节结束揭示' : '本环节无底牌（结辩 / 未发牌）'
      }
      actions={
        <div className="flex items-center gap-2">
          <Badge tone="blind">Commit-Reveal</Badge>
          {card && <Badge tone="neutral">已发牌</Badge>}
        </div>
      }
      className="overflow-hidden"
    >
      <div className="relative">
        {card && (
          <MatrixOrb
            mode="thinking"
            accent={selfSide}
            width={260}
            height={210}
            opacity={0.3}
            spacing={8}
            className="pointer-events-none absolute -right-10 -top-8 z-0"
          />
        )}
        <div className="relative z-[1]">
          {!card ? (
            <div data-testid="hud-no-card">
              <EmptyState
                title="本环节没有底牌"
                detail="结辩环节无需发牌；若比赛尚未开始，请等待教师端开赛。"
              />
            </div>
          ) : card.kind === 'CONSTRUCTIVE' ? (
            <ConstructiveBody
              card={card.card}
              selfSide={selfSide}
              checkedEvidenceIds={checkedEvidenceIds}
              trapOpen={trapOpen}
              onToggleTrap={onToggleTrap}
              onToggleEvidence={onToggleEvidence}
            />
          ) : card.kind === 'CROSS_EXAM' ? (
            <CrossExBody card={card.card} trapOpen={trapOpen} onToggleTrap={onToggleTrap} />
          ) : (
            <SandboxBody
              card={card.card}
              perspective={card.perspective}
              checkedEvidenceIds={checkedEvidenceIds}
              trapOpen={trapOpen}
              onToggleTrap={onToggleTrap}
              onToggleEvidence={onToggleEvidence}
            />
          )}
        </div>
      </div>
    </Panel>
  );
}

/* ------------------------------- 申论策略卡 ------------------------------- */

function ConstructiveBody({
  card,
  selfSide,
  checkedEvidenceIds,
  trapOpen,
  onToggleTrap,
  onToggleEvidence,
}: {
  card: ConstructiveStrategyCard;
  selfSide: Side;
  checkedEvidenceIds: readonly string[];
  trapOpen: boolean;
  onToggleTrap: () => void;
  onToggleEvidence: (evidenceId: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="micro-label">{card.cardId}</span>
          <Badge tone="blind">{ARCHETYPE_LABEL[card.archetype]}</Badge>
          <Badge tone={selfSide === 'AFF' ? 'aff' : 'neg'}>{card.targetRole}</Badge>
        </div>
        <h3 className="mt-2 text-[22px] font-semibold leading-snug tracking-tight text-metal">
          {card.title}
        </h3>
        {card.pillTags && card.pillTags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {card.pillTags.map((tag) => (
              <span key={tag} className="chip">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="lens-inset px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="status-dot status-dot--live" />
          <span className="micro-label text-slate-300">CORE MISSION / 核心战略</span>
        </div>
        <p className="clamp-3 mt-2 text-[13px] leading-relaxed text-slate-200">{card.coreMission}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <ScriptBlock tone="aff" label="01 / 立论锚点" text={card.modularScriptBlocks.headerAnchor} />
        <ScriptBlock tone="neutral" label="02 / 主体模块" text={card.modularScriptBlocks.bodyModule} />
        <ScriptBlock tone="blind" label="03 / 预埋陷阱" text={card.modularScriptBlocks.footerTrap} />
      </div>

      <TrapAccordion
        open={trapOpen}
        onToggle={onToggleTrap}
        title="隐藏反杀锦囊"
        hint={card.hiddenCounterTrap.triggerCondition}
      >
        <div className="space-y-2">
          <p className="text-[11px] text-[#C7D2FE]">触发条件：{card.hiddenCounterTrap.triggerCondition}</p>
          <p className="clamp-3 text-[13px] leading-relaxed text-slate-200">
            反制话术：{card.hiddenCounterTrap.counterResponse}
          </p>
        </div>
      </TrapAccordion>

      <EvidenceList
        evidences={card.mandatoryEvidences.map((evidence) => ({
          id: evidence.id,
          title: evidence.shortTitle,
          detail: evidence.exactQuoteOrStats,
          keywords: evidence.mustIncludeKeywords,
        }))}
        checkedEvidenceIds={checkedEvidenceIds}
        onToggleEvidence={onToggleEvidence}
      />
    </div>
  );
}

/* ------------------------------- 质询任务卡 ------------------------------- */

function CrossExBody({
  card,
  trapOpen,
  onToggleTrap,
}: {
  card: CrossExTaskCard;
  trapOpen: boolean;
  onToggleTrap: () => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="micro-label">{card.cardId}</span>
          <Badge tone="warning">{card.mode === 'TARGETED_COUNTER' ? '定向克制' : '全盲侦察'}</Badge>
          <Badge tone="neutral">{card.targetRole}</Badge>
          {card.targetOpponentArchetype && (
            <Badge tone="blind">{ARCHETYPE_LABEL[card.targetOpponentArchetype]}</Badge>
          )}
        </div>
        <h3 className="mt-2 text-[22px] font-semibold tracking-tight text-metal">
          {card.mode === 'TARGETED_COUNTER' ? '定向克制题链' : '全盲侦察题链'}
        </h3>
      </div>

      <ThreeStepChain chain={card.questionChain} />

      <div className="border-l border-[#FB7185]/45 pl-4">
        <div className="flex items-center gap-2">
          <span className="status-dot bg-[#FB7185] shadow-[0_0_8px_rgba(251,113,133,0.7)]" />
          <span className="micro-label text-[#FECDD3]">TABOO / 绝对雷区</span>
        </div>
        <p className="clamp-3 mt-1.5 text-[13px] leading-relaxed text-slate-200">{card.tabooWarning}</p>
      </div>

      {card.intelHint && (
        <div className="border-l border-[#A5B4FC]/45 pl-4">
          <div className="flex items-center gap-2">
            <span className="status-dot bg-[#A5B4FC] shadow-[0_0_8px_rgba(165,180,252,0.7)]" />
            <span className="micro-label text-[#C7D2FE]">INTEL / 情报提示</span>
          </div>
          <p className="clamp-3 mt-1.5 text-[13px] leading-relaxed text-slate-200">{card.intelHint}</p>
        </div>
      )}

      <TrapAccordion open={trapOpen} onToggle={onToggleTrap} title="隐藏反杀锦囊" hint="收束判准">
        <p className="clamp-3 text-[13px] leading-relaxed text-slate-200">
          第三刀收束：{card.questionChain.step3Crystallize}
        </p>
      </TrapAccordion>
    </div>
  );
}

function ThreeStepChain({ chain }: { chain: CrossExQuestionChain }) {
  const steps: { label: string; text: string }[] = [
    { label: '第一刀 · 锁前提', text: chain.step1LockPremise },
    { label: '第二刀 · 逼两难', text: chain.step2ForceDilemma },
    { label: '第三刀 · 夺判准', text: chain.step3Crystallize },
  ];
  return (
    <ol className="space-y-0" data-testid="hud-question-chain">
      {steps.map((step, index) => (
        <li
          key={step.label}
          className={cx('flex gap-4 py-3', index > 0 && 'border-t border-white/[0.05]')}
        >
          <span className="mono mt-0.5 w-6 shrink-0 text-[18px] font-semibold text-metal-dim">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div className="min-w-0">
            <div className="micro-label">{step.label}</div>
            <p className="clamp-3 mt-1 text-[13px] leading-relaxed text-slate-200">{step.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* -------------------------------- 沙盘大纲 -------------------------------- */

function SandboxBody({
  card,
  perspective,
  checkedEvidenceIds,
  trapOpen,
  onToggleTrap,
  onToggleEvidence,
}: {
  card: SandboxCard;
  perspective: Side;
  checkedEvidenceIds: readonly string[];
  trapOpen: boolean;
  onToggleTrap: () => void;
  onToggleEvidence: (evidenceId: string) => void;
}) {
  const view: SandboxPerspective = card.perspectives[perspective];
  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="micro-label">{card.cardId}</span>
          <Badge tone="blind">{SANDBOX_LABEL[card.topic]}</Badge>
          <Badge tone={perspective === 'AFF' ? 'aff' : 'neg'}>{sideLabel(perspective)}视角</Badge>
        </div>
        <h3 className="mt-2 text-[22px] font-semibold tracking-tight text-metal">{card.title}</h3>
      </div>

      <div className="lens-inset px-4 py-3">
        <div className="micro-label">CROSSFIRE / 交锋焦点</div>
        <p className="mt-1.5 text-[13px] leading-relaxed text-slate-300">{card.crossfireQuestion}</p>
      </div>

      <div className="border-l border-[#A5B4FC]/40 pl-4">
        <div className="micro-label text-[#C7D2FE]">己方视角</div>
        <p className="mt-1 text-[13px] leading-relaxed text-slate-200">{view.label}</p>
      </div>

      <ol className="space-y-0" data-testid="hud-sandbox-outline">
        {view.threeTurnOutline.map((turn, index) => (
          <li
            key={turn.turn}
            className={cx('py-3', index > 0 && 'border-t border-white/[0.05]')}
          >
            <div className="flex items-center gap-3">
              <span className="mono text-[18px] font-semibold text-metal-dim">
                {String(turn.turn).padStart(2, '0')}
              </span>
              <span className="text-[12px] font-medium text-slate-200">{turn.header}</span>
            </div>
            <p className="clamp-3 mt-1.5 pl-9 text-[13px] leading-relaxed text-slate-300">{turn.keyClaim}</p>
            {turn.counterToOpponent && (
              <p className="mt-1 pl-9 text-[11px] text-slate-500">预判：{turn.counterToOpponent}</p>
            )}
          </li>
        ))}
      </ol>

      <TrapAccordion open={trapOpen} onToggle={onToggleTrap} title="对方拆招预判" hint="三回合反制">
        <p className="clamp-3 text-[13px] leading-relaxed text-slate-200">
          {view.threeTurnOutline[0]?.counterToOpponent ?? '按回合要点逐条拆解对方主张。'}
        </p>
      </TrapAccordion>

      <EvidenceList
        evidences={view.mustHitEvidenceIds.map((id) => ({
          id,
          title: id,
          detail: '必打数据（引用论据库）',
          keywords: [],
        }))}
        checkedEvidenceIds={checkedEvidenceIds}
        onToggleEvidence={onToggleEvidence}
      />
    </div>
  );
}

/* -------------------------------- 通用组件 -------------------------------- */

function ScriptBlock({
  label,
  text,
  tone,
}: {
  label: string;
  text: string;
  tone: 'aff' | 'neg' | 'blind' | 'neutral';
}) {
  const dot =
    tone === 'aff'
      ? 'bg-[#7DD3FC] shadow-[0_0_8px_rgba(125,211,252,0.7)]'
      : tone === 'blind'
        ? 'bg-[#A5B4FC] shadow-[0_0_8px_rgba(165,180,252,0.7)]'
        : 'bg-slate-500';
  return (
    <div className="lens-inset px-4 py-3">
      <div className="flex items-center gap-2">
        <span className={cx('h-1 w-1 rounded-full', dot)} />
        <span className="micro-label">{label}</span>
      </div>
      <p className="clamp-3 mt-2 text-[13px] leading-relaxed text-slate-200">{text}</p>
    </div>
  );
}

function TrapAccordion({
  open,
  onToggle,
  title,
  hint,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(
        'relative overflow-hidden rounded-[12px] pl-4 transition-colors',
        open ? 'bg-[#A5B4FC]/[0.07]' : 'bg-white/[0.015]',
      )}
    >
      <span
        className={cx(
          'absolute inset-y-0 left-0 w-px transition-all',
          open
            ? 'bg-gradient-to-b from-[#C7D2FE] via-[#A5B4FC] to-transparent shadow-[0_0_12px_rgba(165,180,252,0.6)]'
            : 'bg-gradient-to-b from-[#A5B4FC]/50 to-transparent',
        )}
      />
      <button
        type="button"
        data-testid="hud-counter-trap"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-2 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-[12px] font-medium text-[#C7D2FE]">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M6.8 0.5 2 6.6h3.1L5.2 11.5 10 5.4H6.9L6.8 0.5Z" fill="currentColor" fillOpacity="0.85" />
          </svg>
          {title}
          <kbd className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] text-slate-400 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]">
            Tab
          </kbd>
        </span>
        <span className="micro-label">{open ? 'COLLAPSE' : 'EXPAND'}</span>
      </button>
      {!open && hint && <p className="clamp-3 px-2 pb-3 text-[11px] text-slate-500">{hint}</p>}
      {open && (
        <div
          data-testid="hud-counter-trap-content"
          className="border-t border-white/[0.06] px-2 py-3"
        >
          {children}
        </div>
      )}
    </div>
  );
}

export interface EvidenceRow {
  id: string;
  title: string;
  detail: string;
  keywords: string[];
}

function EvidenceList({
  evidences,
  checkedEvidenceIds,
  onToggleEvidence,
}: {
  evidences: EvidenceRow[];
  checkedEvidenceIds: readonly string[];
  onToggleEvidence: (evidenceId: string) => void;
}) {
  const checked = new Set(checkedEvidenceIds);
  if (evidences.length === 0) {
    return <EmptyState title="本卡没有必打论据" detail="按卡片正文组织论证即可。" />;
  }
  return (
    <div data-testid="hud-evidence-list">
      <div className="flex items-center justify-between">
        <h4 className="micro-label text-slate-300">MANDATORY CITATIONS / 必打论据</h4>
        <span className="mono text-[10px] text-slate-500">
          {evidences.filter((evidence) => checked.has(evidence.id)).length} / {evidences.length}
        </span>
      </div>
      <ul className="mt-2">
        {evidences.map((evidence, index) => {
          const isChecked = checked.has(evidence.id);
          return (
            <li key={evidence.id} className={cx(index > 0 && 'border-t border-white/[0.05]')}>
              <button
                type="button"
                data-testid="hud-evidence-toggle"
                data-evidence-id={evidence.id}
                aria-pressed={isChecked}
                onClick={() => onToggleEvidence(evidence.id)}
                className="group flex w-full items-start gap-3 py-3 text-left"
              >
                <span
                  className={cx(
                    'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] transition-all',
                    isChecked
                      ? 'bg-[#F8FAFC] text-[#050507] shadow-[0_0_14px_rgba(255,255,255,0.45)]'
                      : 'text-transparent shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] group-hover:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.35)]',
                  )}
                  aria-hidden
                >
                  ✓
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="mono text-[9px] text-slate-600">{String(index + 1).padStart(2, '0')}</span>
                    <span className={cx('text-[12.5px] font-medium', isChecked ? 'text-[#F0FDF4]' : 'text-slate-200')}>
                      {evidence.title}
                    </span>
                    {evidence.keywords.slice(0, 3).map((keyword) => (
                      <span key={keyword} className="chip font-mono">
                        {keyword}
                      </span>
                    ))}
                  </span>
                  <span className="mt-1 block text-[11px] leading-relaxed text-slate-500">{evidence.detail}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
